import { onMounted, onUnmounted } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

const KEYBOARD_BODY_CLASS = 'keyboard-open';
const KEYBOARD_SLIDE_CLASS = 'keyboard-slide';

/** body.keyboard-slide（translateY）を有効にする Capacitor プラットフォーム */
const KEYBOARD_SLIDE_PLATFORMS = ['ios', 'android'];

/**
 * 直上寄せ（#modal1）時に、入力欄下端とキーボード上端の間へ確保する余白(px)。
 * 商品が少ないとき入力欄下の確認ボタンも視界に入るよう多めに取る。
 * ※実機での見え方に合わせて調整する暫定値。
 */
const ABOVE_KEYBOARD_MARGIN_PX = 72;

/** キーボード入力対象外の input type */
const NON_TEXT_INPUT_TYPES = new Set([
  'checkbox',
  'radio',
  'hidden',
  'button',
  'submit',
  'reset',
  'image',
  'file',
  'color',
  'range',
]);

const isTextInput = (el) => {
  if (!el || el.readOnly || el.disabled) return false;

  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;

  if (tag !== 'INPUT') return false;

  const type = (el.type || 'text').toLowerCase();
  return !NON_TEXT_INPUT_TYPES.has(type);
};

/**
 * キーボードを除いた可視領域の高さ。
 * - 基本: フォーカス時 innerHeight - keyboardHeight
 * - adjustResize 済みで innerHeight が既に可視領域のときは、減算結果が過小になるため
 *   キーボード表示時点の innerHeight をそのまま可視高さとする
 */
const getVisibleHeight = (innerHeightAtFocus, keyboardHeight, innerHeightAtApply) => {
  const currentInnerHeight = innerHeightAtApply > 0
    ? innerHeightAtApply
    : innerHeightAtFocus;

  const fromSubtract = Math.max(0, innerHeightAtFocus - keyboardHeight);

  if (
    currentInnerHeight > 0
    && fromSubtract < currentInnerHeight * 0.35
  ) {
    return currentInnerHeight;
  }

  return fromSubtract > 0 ? fromSubtract : currentInnerHeight;
};

/**
 * 上方向へのスライド量(px)を算出する。
 * - alignAboveKeyboard = false（既定）: フォーカス中入力の中心が (可視高さ / 2) に来る中央寄せ。
 * - alignAboveKeyboard = true: 入力欄下端がキーボード上端付近（可視領域下端の少し上）に来る直上寄せ。
 *   ABOVE_KEYBOARD_MARGIN_PX 分の余白を確保し、入力欄下の確認ボタンも視界に入りやすくする。
 */
const computeSlideOffsetPx = (el, innerHeightAtFocus, keyboardHeight, innerHeightAtApply, alignAboveKeyboard = false) => {
  const visibleHeight = getVisibleHeight(innerHeightAtFocus, keyboardHeight, innerHeightAtApply);
  const rect = el.getBoundingClientRect();

  if (alignAboveKeyboard) {
    // 直上寄せ: 入力欄下端を (可視領域下端 - 余白) まで持ち上げる
    const targetBottomY = visibleHeight - ABOVE_KEYBOARD_MARGIN_PX;
    return Math.max(0, Math.round(rect.bottom - targetBottomY));
  }

  // 中央寄せ（既存挙動）
  const targetCenterY = visibleHeight / 2;
  const currentCenterY = rect.top + rect.height / 2;

  return Math.max(0, Math.round(currentCenterY - targetCenterY));
};

/**
 * ネイティブアプリ（Android / iOS）でキーボード表示中の UI 調整。
 * - keyboardWillShow: フッター (.com_app_nav1) を非表示（body.keyboard-open）
 * - keyboardDidShow: KEYBOARD_SLIDE_PLATFORMS のみ translateY（body.keyboard-slide）
 */
export function useKeyBoardFooter() {
  let willShowListener = null;
  let didShowListener = null;
  let hideListener = null;
  let lastFocusedInput = null;
  let innerHeightAtFocus = 0;
  const isKeyboardSlideEnabled = () => KEYBOARD_SLIDE_PLATFORMS.includes(Capacitor.getPlatform());

  const clearKeyboardSlide = () => {
    document.body.classList.remove(KEYBOARD_SLIDE_CLASS);
    // モーダル側に付与したスライドクラスも除去（閉じた後に transform が残らないように）
    document
      .querySelectorAll(`.modal.${KEYBOARD_SLIDE_CLASS}`)
      .forEach((modal) => modal.classList.remove(KEYBOARD_SLIDE_CLASS));
    document.documentElement.style.removeProperty('--keyboard-translate-y');
  };

  const applyKeyboardSlide = (keyboardHeight) => {
    const el = isTextInput(document.activeElement)
      ? document.activeElement
      : lastFocusedInput;

    if (!isTextInput(el) || keyboardHeight <= 0) {
      clearKeyboardSlide();
      return;
    }

    // 同一成分モーダル(#modal1)表示中のみ直上寄せ。他画面・他モーダルは従来の中央寄せ。
    const isModal1 = !!document.querySelector('#modal1.show');

    const slideOffsetPx = computeSlideOffsetPx(
      el,
      innerHeightAtFocus,
      keyboardHeight,
      window.innerHeight,
      isModal1,
    );

    if (slideOffsetPx <= 0) {
      return;
    }

    document.documentElement.style.setProperty('--keyboard-translate-y', `${slideOffsetPx}px`);

    // モーダル表示中はスライド対象を body ではなく表示中モーダルの .modal-content にする
    const activeModal = document.querySelector('.modal.show');
    if (activeModal) {
      document.body.classList.remove(KEYBOARD_SLIDE_CLASS);
      activeModal.classList.add(KEYBOARD_SLIDE_CLASS);
    } else {
      document.body.classList.add(KEYBOARD_SLIDE_CLASS);
    }
  };

  const setKeyboardOpen = (open) => {
    document.body.classList.toggle(KEYBOARD_BODY_CLASS, open);

    if (!open) {
      clearKeyboardSlide();
    }
  };

  const onFocusIn = (event) => {
    if (!isTextInput(event.target)) return;

    lastFocusedInput = event.target;
    innerHeightAtFocus = window.innerHeight;
  };

  onMounted(async () => {
    const platform = Capacitor.getPlatform();
    if (platform !== 'android' && platform !== 'ios') return;
    if (!Capacitor.isPluginAvailable('Keyboard')) return;

    innerHeightAtFocus = window.innerHeight;
    document.addEventListener('focusin', onFocusIn);

    willShowListener = await Keyboard.addListener('keyboardWillShow', () => {
      if (innerHeightAtFocus <= 0) {
        innerHeightAtFocus = window.innerHeight;
      }
      setKeyboardOpen(true);
    });
    didShowListener = await Keyboard.addListener('keyboardDidShow', (info) => {
      if (!isKeyboardSlideEnabled()) return;
      if (!document.body.classList.contains(KEYBOARD_BODY_CLASS)) return;

      applyKeyboardSlide(info.keyboardHeight ?? 0);
    });
    hideListener = await Keyboard.addListener('keyboardWillHide', () => {
      setKeyboardOpen(false);
    });
  });

  onUnmounted(async () => {
    document.removeEventListener('focusin', onFocusIn);
    lastFocusedInput = null;
    innerHeightAtFocus = 0;

    await willShowListener?.remove();
    await didShowListener?.remove();
    await hideListener?.remove();
    willShowListener = null;
    didShowListener = null;
    hideListener = null;
    document.body.classList.remove(KEYBOARD_BODY_CLASS);
    clearKeyboardSlide();
  });
}
